import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { useTranslation } from './i18n/useTranslation';
import { ToastProvider } from './context/ToastContext';
import Login from './components/Login';
// import Register from './components/Register'; // Commented out - register page hidden
import UserDashboard from './components/UserDashboard';
import Prices from './components/Prices';
import AdminDashboard from './components/AdminDashboard';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WorkingHoursBanner from './components/WorkingHoursBanner';
import { WorkingHoursProvider } from './context/WorkingHoursContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return user && ['admin', 'super'].includes(user.role) ? children : <Navigate to="/" />;
};

const SiteBlockedPage = ({ config }) => {
  const { t, language } = useTranslation();
  const isRTL = language === 'ar';
  const mode = config?.mode || 'maintenance';
  const message = config?.messages?.[mode]?.[language] || t(`siteBlock.${mode}.message`);

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-blue-50 via-white to-gray-50 px-4 py-8 sm:py-12" dir={isRTL ? 'rtl' : 'ltr'}>
      <section className="mx-auto w-full max-w-4xl">
        <div className="mb-6 sm:mb-8 rounded-lg border border-blue-100 bg-white/80 px-4 py-3 shadow-sm backdrop-blur">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-blue-700">{t('siteBlock.label')}</p>
            <p className="text-sm text-gray-500">{t('siteBlock.subtitle')}</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-blue-100 bg-white shadow-xl">
          <div className="h-2 bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-400" />
          <div className="p-8 text-center sm:p-12 lg:p-16">
            <div className="mb-8 flex justify-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-100 text-blue-700 ring-8 ring-blue-50 sm:h-24 sm:w-24">
                <svg className="h-10 w-10 sm:h-12 sm:w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M4 11h16M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zm7 10h.01M8 15h.01M16 15h.01" />
                </svg>
              </div>
            </div>
            <p className="mb-4 text-base font-bold uppercase tracking-wide text-blue-700 sm:text-lg">
              {t('siteBlock.badge')}
            </p>
            <h1 className="mb-6 text-4xl font-extrabold text-gray-950 sm:text-5xl">
              {t(`siteBlock.${mode}.title`)}
            </h1>
            <p className="mx-auto max-w-3xl text-xl leading-10 text-gray-600 sm:text-2xl sm:leading-[3rem]">
              {message}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

// React Router keeps the previous scroll offset on navigation - reset it so a
// new page always opens at the top.
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
};

// The hours banner is for patients - staff pages (login, admin) don't need it.
const STAFF_PATHS = ['/admin', '/login'];

const PublicWorkingHours = () => {
  const location = useLocation();
  const isStaffPath = STAFF_PATHS.some(path => location.pathname.startsWith(path));
  if (isStaffPath) return null;
  return <WorkingHoursBanner />;
};

const SiteAccessGate = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [siteAccessConfig, setSiteAccessConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSiteAccessStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/site/access-status`);
        setSiteAccessConfig(response.data);
      } catch (error) {
        console.error('Error fetching site access status:', error);
        setSiteAccessConfig({ enabled: false });
      } finally {
        setLoading(false);
      }
    };

    fetchSiteAccessStatus();
  }, []);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const isAdmin = ['admin', 'super'].includes(user?.role);
  const isAuthPath = location.pathname === '/login';
  const isAdminPath = location.pathname.startsWith('/admin');

  if (siteAccessConfig?.enabled && !isAdmin && !isAuthPath && !isAdminPath) {
    return <SiteBlockedPage config={siteAccessConfig} />;
  }

  return children;
};

function App() {
  return (
    <LanguageProvider>
      <ToastProvider>
        <AuthProvider>
          <WorkingHoursProvider>
            <Router>
              <ScrollToTop />
              <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                {/* Doctor's working hours - public pages only */}
                <PublicWorkingHours />
                <div className="flex-1">
                  <SiteAccessGate>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      {/* <Route path="/register" element={<Register />} /> */} {/* Commented out - register page hidden */}
                      <Route path="/" element={<UserDashboard />} />
                      <Route path="/prices" element={<Prices />} />
                      <Route
                        path="/admin"
                        element={
                          <AdminRoute>
                            <AdminDashboard />
                          </AdminRoute>
                        }
                      />
                      {/* Catch-all route - redirect wrong URLs to home */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </SiteAccessGate>
                </div>
                <Footer />
              </div>
            </Router>
          </WorkingHoursProvider>
        </AuthProvider>
      </ToastProvider>
    </LanguageProvider>
  );
}

export default App;
